import { Fragment, useState, useContext, useEffect } from "react";
import { StateContext } from "../../services/context";
import { Transition, Dialog } from "@headlessui/react";
import { classNames } from "../../utils/utils";
import { CancelIcon, AddCircleIcon } from "../../utils/iconFactory";
import { CustomInput } from "../Elements/CustomInputs";
import { useTheme } from "../../utils/themeUtils";
import { PrimaryButton, SecondaryButton } from "../Elements/CustomButtons";
import { DeleteIcon } from "../../utils/iconFactory";

interface AddWordProps {
  inputWord: string;
  listOfWords: string[];
  listSetter: React.Dispatch<React.SetStateAction<string[]>>;
  wordSetter: React.Dispatch<React.SetStateAction<string>>;
}

interface KeyDownProps extends AddWordProps {
  event: React.KeyboardEvent<HTMLInputElement>;
}

export function EditMoreSettings({ isVisible, onClose, maxChar, setMaxChar, avoidedWords, setAvoidedWords, usedWords, setUsedWords }: 
  { isVisible: boolean; onClose: () => void, maxChar: any, setMaxChar: (e: any) => void, avoidedWords: any[], setAvoidedWords: (e: any) => void, usedWords: any[], setUsedWords: (e: any) => void}){
  const { themeString } = useTheme()
  const {contentType} = useContext(StateContext)

  const [useWordsInput, setUseWordsInput] = useState<string>('');
  const [avoidWordsInput, setAvoidWordsInput] = useState<string>('');

  useEffect(() => {
    setMaxChar(contentType.content.maxLength?.[0] ?? 0)
    setAvoidedWords([])
    setUsedWords([])

  }, [contentType])

  function removeWord(word: any, list: string[], fun: (newList: string[]) => void) {
    fun(list.filter(item => item !== word));
  }

  const handleAddWord = ({ inputWord, listOfWords, listSetter, wordSetter }: AddWordProps) => {
    if (!listOfWords.includes(inputWord)) {
      listSetter([...listOfWords, inputWord]);
      wordSetter("");
    }
  };

  const handleKeyDown = ({ event, inputWord, listOfWords, listSetter, wordSetter }: KeyDownProps) => 
    {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        handleAddWord({inputWord: inputWord, listOfWords: listOfWords, listSetter: listSetter, wordSetter: wordSetter});
      }
  };

  return (
    <Transition appear show={isVisible} as={Fragment}>
      <Dialog as="div" className={classNames("relative z-40", themeString)} onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="relative h-[615px] w-[840px] rounded-[22px] bg-backgroundWhite py-8 text-left align-middle transform transition-all overflow-hidden modal-shadow">
                <Dialog.Title
                  as="h3"
                  className="text-textMain text-style-h3 border-b border-gray10 pb-4 mx-6 overflow-hidden"
                >
                  További beállítások
                </Dialog.Title>
                <button className='absolute top-9 right-6' onClick={onClose}>
                  <CancelIcon className='h-6 w-6 text-gray100 fill-current' />
                </button>

                <div id='content' style={{ maxHeight: "calc(100% - 50px)" }} className="flex flex-col gap-4 px-6 pt-4 overflow-y-auto no-scrollbar">
                  <section id='avoid-words' className="bg-backgroundDarkGray rounded-[18px] p-4 flex flex-col gap-4">
                    <div id='header-group'>
                      <p className="text-mainText text-style-sub1">Kerülendő szavak hozzáadása</p>
                      {
                        avoidedWords.length !== 0 &&
                        <div id='avoided-word-row' className="flex flex-wrap gap-x-2 text-gray150 text-style-body2">
                          {avoidedWords.map((word, index) => (
                            <div id='single-word-wrapper' className="group">
                              <div id='background-padding' className="flex py-2 rounded-[12px] group-hover:p-2 group-hover:bg-backgroundWhite transform transition-all">
                                <p id='word' >{word}</p>
                                {index < avoidedWords.length - 1 && <span id='comma' className="block group-hover:hidden">, </span>}
                                <button 
                                  id='delete-button'
                                  className="hidden group-hover:block"
                                  onClick={() => removeWord(word, avoidedWords, setAvoidedWords)}
                                >
                                  <DeleteIcon id='delete-icon' className="flex-none h-5 w-5 ml-2 text-tertiaryRed fill-current" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      }
                    </div>

                    <div className="flex gap-2">
                      <CustomInput 
                        className="min-w-[352px]"
                        type="text"
                        value={avoidWordsInput}
                        customAction={() => setAvoidWordsInput("")}
                        onChange={(e) => setAvoidWordsInput(e.target.value)}
                        onKeyDown={e => handleKeyDown({event:e, inputWord: avoidWordsInput, listOfWords: avoidedWords, listSetter: setAvoidedWords, wordSetter: setAvoidWordsInput})}
                        spellCheck={false}
                      />
                      <PrimaryButton 
                        buttonInfo={{buttonIcon: AddCircleIcon }}
                        onClick={() => {handleAddWord({inputWord: avoidWordsInput, listOfWords: avoidedWords, listSetter: setAvoidedWords, wordSetter: setAvoidWordsInput})}}
                      />
                    </div>
                  </section>
                  <section id='use-words' className="bg-backgroundDarkGray rounded-[18px] p-4 flex flex-col gap-4">
                    <div id='header-group' className=" transform transition-all">
                        <p className="text-mainText text-style-sub1">Használandó szavak hozzáadása</p>
                        {
                          usedWords.length !== 0 &&
                          <div id='used-word-row' className="flex flex-wrap gap-x-2 text-gray150 text-style-body2">
                            {usedWords.map((word, index) => (
                              <div id='single-word-wrapper' className="group">
                                <div id='background-padding' className="flex py-2 rounded-[12px] group-hover:p-2 group-hover:bg-backgroundWhite transform transition-all">
                                  <p id='word' >{word}</p>
                                  {index < usedWords.length - 1 && <span id='comma' className="block group-hover:hidden">, </span>}
                                  <button 
                                    id='delete-button'
                                    className="hidden group-hover:block"
                                    onClick={() => removeWord(word, usedWords, setUsedWords)}                                    
                                  >
                                    <DeleteIcon id='delete-icon' className="flex-none h-5 w-5 ml-2 text-tertiaryRed fill-current" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        }
                      </div>

                    <div className="flex gap-2">
                      <CustomInput 
                        className="min-w-[352px]" 
                        type="text"
                        value={useWordsInput}
                        customAction={() => setUseWordsInput("")}
                        onChange={(e) => setUseWordsInput(e.target.value)}
                        onKeyDown={e => handleKeyDown({event:e, inputWord: useWordsInput, listOfWords: usedWords, listSetter: setUsedWords, wordSetter: setUseWordsInput})}
                        spellCheck={false}
                      />
                      <PrimaryButton 
                        buttonInfo={{buttonIcon: AddCircleIcon }} 
                        onClick={() => {handleAddWord({inputWord: useWordsInput, listOfWords: usedWords, listSetter: setUsedWords, wordSetter: setUseWordsInput})}}
                      />
                    </div>
                  </section>
                  {contentType.content.maxLength &&
                    <section id='max-chars' className="bg-backgroundDarkGray rounded-[18px] p-4 flex flex-col gap-4 w-full">
                      <p className="text-mainText text-style-sub1">Maximum karakterszám</p>
                      <div className="flex flex-row w-full justify-evenly bg-backgroundLightGray rounded-[12px]">
                        {contentType.content.maxLength.map((number, idx) => (
                        <button key={idx} className={classNames(maxChar === number ? "bg-textSecondary text-backgroundWhite text-style-body2-bold" :
                            "hover:bg-gray10 text-style-body2",
                            "w-full pt-[13px] pb-[11px] rounded-[12px]")}
                            onClick={() => setMaxChar(number)}
                          >
                            {number}
                          </button>
                        ))}
                      </div>
                    </section>
                  }
                  <button
                    onClick={onClose}
                    className={classNames("px-4 py-3 rounded-[12px] w-full text-style-body2-bold", "bg-primaryAction text-backgroundWhite primary-button-drop-shadow primary-button-hover-drop-shadow")}
                  >
                    Mentés
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}